import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { useLifecycleActions } from '../state/actions/lifecycle';

export function Changes() {
  const { cases } = useAppData();
  const { approveChange } = useLifecycleActions();
  const user = getCurrentUser()!;
  const canApprove = user.canApprove;

  // Schema default for `pending_changes` is `'{}'::jsonb` (truthy empty
  // object) — `{}.filter` crashes. See migration 0002 for the schema fix
  // and migration 0003 for the backfill; this guard handles either side.
  const pending = cases.flatMap(c => {
    const list = Array.isArray(c.pendingChanges) ? c.pendingChanges : [];
    return list.filter((ch: any) => ch.status === 'PENDING').map((ch: any) => ({ ...ch, caseId: c.id, registrant: c.registrant }));
  });
  const history = cases.flatMap(c => {
    const list = Array.isArray(c.changeHistory) ? c.changeHistory : [];
    return list.map((ch: any) => ({ ...ch, caseId: c.id, registrant: c.registrant }));
  });

  // Facet over the change types actually present in the history.
  const changeTypes = Array.from(new Set(history.map((ch: any) => ch.type).filter(Boolean))).sort();
  const { filtered: filteredHistory, bar } = useSearchFilter(
    history,
    (ch: any) => [ch.registrant, ch.caseId, ch.type, ch.details].filter(Boolean).join(' '),
    [
      {
        id: 'type',
        label: 'Change type',
        options: [
          { value: '', label: 'All change types' },
          ...changeTypes.map((t) => ({ value: t as string, label: t as string })),
        ],
        predicate: (ch: any, v) => ch.type === v,
      },
    ],
    { placeholder: 'Search history by registrant, case, type, or details…' },
  );

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Registrant Change Tracking</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>Changes must be reviewed, approved, and synced to Settlement Platform within 24 hours</div>
      {pending.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>Pending Approval ({pending.length})</h3>
          {pending.map((ch: any) => (
            <div key={ch.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <strong>{ch.registrant}</strong> ({ch.caseId}) — <span style={{ ...styles.tag, background: C.infoBg, color: C.info }}>{ch.type}</span>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{ch.details}</div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>Submitted: {fmtDateTime(ch.submittedAt)}</div>
              </div>
              {canApprove && (
                <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => approveChange(ch.caseId, ch.id)}>Approve</button>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Change History</h3>
        <SearchFilterBar {...bar} />
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Registrant</th><th style={styles.th}>Type</th><th style={styles.th}>Details</th><th style={styles.th}>Submitted</th><th style={styles.th}>Approved</th>
          </tr></thead>
          <tbody>
            {filteredHistory.map((ch: any) => (
              <tr key={ch.id}>
                <td style={styles.td}>{ch.registrant}</td>
                <td style={styles.td}><span style={{ ...styles.tag, background: C.infoBg, color: C.info }}>{ch.type}</span></td>
                <td style={styles.td}>{ch.details}</td>
                <td style={styles.td}>{fmtDateTime(ch.submittedAt)}</td>
                <td style={styles.td}>{fmtDateTime(ch.approvedAt)}</td>
              </tr>
            ))}
            {filteredHistory.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>{history.length === 0 ? 'No change history' : 'No changes match the current filters'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
